import React, { useState } from 'react';
import {
  Box,
  Card,
  Typography,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Divider
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import useClarification from '../../../hooks/purchaseInvoices/invoiceManagement/useClarification';

/**
 * Panel for IT managers to respond to clarification requests
 */
const ClarificationResponsePanel = ({ clarification, onResponseSent }) => {
  const [responseText, setResponseText] = useState('');
  const { respondToClarification, isRespondingToClarification } = useClarification();

  if (!clarification || clarification.status !== 'pending') {
    return null;
  }

  const handleSubmitResponse = (approved) => {
    if (!responseText.trim() && !approved) {
      return;
    }

    respondToClarification({
      clarificationId: clarification._id,
      responseText: responseText.trim(),
      approved: approved
    }, {
      onSuccess: () => {
        setResponseText('');
        if (onResponseSent) {
          onResponseSent();
        }
      }
    });
  };

  return (
    <Card sx={{
      p: 3,
      mb: 3,
      bgcolor: 'rgba(255, 152, 0, 0.08)',
      border: '2px solid',
      borderColor: 'warning.main',
      borderRadius: 2
    }}>
      <Typography variant="h6" color="warning.dark" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        🔍 בקשת בירור ממתינה לתגובתך
      </Typography>

      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2">
          <strong>{clarification.requestedBy?.name || 'מאשר'}</strong> שלח לך בקשה לבירור חשבונית זו.
          <br />
          נא לבדוק את פרטי החשבונית ולהגיב.
        </Typography>
      </Alert>

      <Box sx={{ 
        bgcolor: 'background.paper', 
        p: 2, 
        borderRadius: 1, 
        mb: 2,
        border: '1px solid',
        borderColor: 'divider'
      }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          פרטי החשבונית:
        </Typography>
        <Typography variant="body2">
          <strong>מספר חשבונית:</strong> {clarification.invoiceDetails?.booknum || '-'}
        </Typography>
        <Typography variant="body2">
          <strong>ספק:</strong> {clarification.invoiceDetails?.supName || '-'}
        </Typography>
        <Typography variant="body2">
          <strong>חברה:</strong> {clarification.invoiceDetails?.companyName || '-'}
        </Typography>
        {clarification.invoiceDetails?.amount && (
          <Typography variant="body2">
            <strong>סכום:</strong> ₪{Number(clarification.invoiceDetails.amount).toLocaleString()}
          </Typography>
        )}
      </Box>

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        הוסף תגובה:
      </Typography>

      <TextField
        fullWidth
        multiline
        rows={4}
        value={responseText}
        onChange={(e) => setResponseText(e.target.value)}
        placeholder="כתוב את תגובתך כאן..."
        sx={{ mb: 2 }}
        disabled={isRespondingToClarification}
      />

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => handleSubmitResponse(false)}
          disabled={!responseText.trim() || isRespondingToClarification}
          startIcon={isRespondingToClarification ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
        >
          {isRespondingToClarification ? 'שולח...' : 'שלח תגובה'}
        </Button>
      </Box>
    </Card>
  );
};

export default ClarificationResponsePanel;
